<template>
  <div class="tab-content">
    <!-- Month/Year Filter -->
    <div class="filter-row">
      <div class="filter-group">
        <label class="filter-label">Month:</label>
        <select :value="selectedMonth" @input="$emit('update:selectedMonth', Number($event.target.value))" class="filter-select">
          <option v-for="(m, i) in months" :key="i" :value="i + 1">{{ m }}</option>
        </select>
      </div>
      <div class="filter-group">
        <label class="filter-label">Year:</label>
        <select :value="selectedYear" @input="$emit('update:selectedYear', Number($event.target.value))" class="filter-select">
          <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
        </select>
      </div>
    </div>

    <!-- Payment History Table -->
    <div class="table-section">
      <h3 class="table-title">Payment History - {{ months[selectedMonth - 1] }} {{ selectedYear }}</h3>
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading payments...</p>
      </div>
      <div v-else class="table-container">
        <table class="payments-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Basic Salary</th>
              <th>Deductions</th>
              <th>Net Salary</th>
              <th>Payment Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="payments.length === 0">
              <td colspan="7" class="empty-state">
                <p>No payments found for {{ months[selectedMonth - 1] }} {{ selectedYear }}</p>
              </td>
            </tr>
            <tr v-for="payment in payments" :key="payment.expenseID">
              <td><span class="employee-name">{{ payment.employeeName }}</span></td>
              <td>{{ formatCurrency(payment.basicSalary) }}</td>
              <td>{{ formatCurrency(payment.whtDeduction + payment.otherDeductions) }}</td>
              <td><span class="salary">{{ formatCurrency(payment.netSalary || payment.basicSalary - payment.whtDeduction - payment.otherDeductions) }}</span></td>
              <td>{{ formatDate(payment.paymentDate) }}</td>
              <td>
                <span :class="['status-badge', (payment.status || 'pending').toLowerCase()]">{{ payment.status || 'Pending' }}</span>
              </td>
              <td>
                <div class="action-buttons">
                  <button class="action-btn view-btn" title="View">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SalaryPaymentsTab',
  props: {
    selectedMonth: { type: Number, required: true },
    selectedYear: { type: Number, required: true },
    payments: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  data () {
    return {
      months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    }
  },
  computed: {
    yearOptions () {
      const current = new Date().getFullYear()
      return [current - 2, current - 1, current, current + 1]
    }
  },
  methods: {
    formatCurrency (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value || 0)
    },
    formatDate (dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    }
  }
}
</script>

<style scoped>
.filter-row {
  display: flex;
  gap: 20px;
  align-items: center;
  margin-bottom: 24px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  background: white;
  cursor: pointer;
  min-width: 120px;
}

.filter-select:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.table-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 20px;
}

.table-container { overflow-x: auto; }

.payments-table { width: 100%; border-collapse: collapse; }
.payments-table thead tr { border-bottom: 1px solid #e5e7eb; }

.payments-table th {
  padding: 12px 0;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.payments-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.2s ease;
}

.payments-table tbody tr:hover { background-color: #f9fafb; }

.payments-table td {
  padding: 16px 0;
  font-size: 14px;
  color: #1f2937;
}

.employee-name { font-weight: 600; color: #1f2937; }
.salary { font-weight: 600; color: #1f2937; }

.status-badge {
  display: inline-block;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.paid { background: #dcfce7; color: #166534; }
.status-badge.pending { background: #fef3c7; color: #92400e; }

.empty-state { padding: 40px 20px; text-align: center; color: #6b7280; }

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
}

.spinner {
  width: 32px; height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.loading-state p { margin-top: 12px; color: #6b7280; }

.action-buttons { display: flex; gap: 8px; }

.action-btn {
  width: 36px; height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #6b7280;
}

.action-btn:hover { border-color: #d1d5db; background: #f9fafb; }
.view-btn:hover { color: #2563eb; }

@media (max-width: 768px) {
  .filter-row { flex-wrap: wrap; }
}
</style>
